import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Phone, Layers, ListOrdered, Award, ShieldCheck, AlertTriangle, FileText } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { pages as pageContent } from '@/data/pageContent';

const Methodology = () => {
  const route = '/methodology';
  const canonical = `https://energyperformancedirect.co.uk${route}`;
  const phone = '01392 980077';

  // pageContent is the single source of truth for FAQs (and their FAQPage
  // schema) on this route — the SSG build script consumes the same module,
  // so build-time HTML and React runtime render an identical FAQ list.
  // One FAQ is augmented at render time with an in-line case-study Link
  // (JSX) since the module has to remain plain strings for the Node SSG.
  const routeContent = pageContent[route];
  const faqs = routeContent.faqs.map((f) => {
    if (f.q === 'How is this methodology different from a standard EPC recommendations report?') {
      return {
        q: f.q,
        a: (
          <>
            {f.a} See a full worked example in the{' '}
            <Link to="/case-study-exeter-office-g-to-a" className="text-green-700 underline">
              Exeter G-to-A office case study
            </Link>
            .
          </>
        ),
      };
    }
    return f;
  });

  const criteria = [
    { label: 'EPC Points Gained', desc: 'SAP or SBEM asset-rating improvement — best measure ranks highest.' },
    { label: 'Energy Saving (kWh/m²/yr)', desc: 'Absolute energy reduction against the baseline model.' },
    { label: 'CO₂ Reduction', desc: 'Carbon saved (kg/m²/yr non-domestic; tonnes/yr domestic).' },
    { label: 'Cost Saving (£/yr)', desc: 'Annual bill impact using current fuel prices.' },
    { label: 'Payback Period', desc: 'Installed cost ÷ annual saving; 7-year test flag applied for non-domestic.' },
  ];

  const rafts = [
    {
      title: 'RAFT 1 — Priority Measures',
      target: 'Immediate compliance + heating and fabric basics',
      desc: 'Most impactful, most cost-effective measures. Where a regulatory minimum applies (Band E now; proposed EPC B by 2031 for non-domestic buildings over 1,000 m², and domestic Band C from 1 October 2030) Raft 1 achieves it. Domestic: E compliance / quick wins. Non-domestic: E with 7-year payback compliance.',
    },
    {
      title: 'RAFT 2 — Secondary Improvements',
      target: 'Band C mid-term target',
      desc: 'Cumulative with Raft 1. Fabric upgrades and controls added. Domestic: EPC C for 2030. Non-domestic: interim Band C milestone.',
    },
    {
      title: 'RAFT 3 — Deep Retrofit',
      target: 'Band A or B long-term pathway',
      desc: 'Cumulative with Rafts 1 and 2. Adds generation (ASHP, PV, DHW electrification). Domestic: near-Band A. Non-domestic: Band B or A.',
    },
  ];

  const services = [
    {
      code: 'DEAR',
      full: 'Domestic Energy Advice Report',
      for: 'Residential landlords under domestic MEES',
      model: 'RdSAP 10',
      linkTo: '/services/domestic_advice',
    },
    {
      code: 'NDEAR',
      full: 'Non-Domestic Energy Advice Report',
      for: 'Commercial landlords under non-domestic MEES, and owner-occupiers with a specific operational or capital objective (cold rooms, carbon reduction, plant efficiency)',
      model: 'SBEM (iSBEM), with heat-loss calculations where the brief requires',
      linkTo: '/mees-commercial-buildings',
    },
  ];

  const limitations = [
    { title: 'RdSAP / SAP', points: ['Standard occupancy assumptions — does not reflect individual household behaviour.', 'Heating controls captured for efficiency, not operational-hour savings.', 'Does not model time-of-use tariffs (HHRSH partly tariff-driven).'] },
    { title: 'iSBEM / SBEM', points: ['Lighting energy can show modelled BER worsening despite real-world cost savings.', 'Air permeability defaults to 15 or 25 m³/(h·m²) if not measured on-site.', 'Auxiliary loads (pumps, fans) not fully dynamic; controls optimisation not fully captured.'] },
    { title: 'Cost & Fuel Prices', points: ['Indicative costs from published 2024–2026 market benchmarks; client-provided quotes replace estimates.', 'Fuel prices 2026: gas 7.5–8.0 p/kWh; electricity 23–25 p/kWh.', 'Long-term price forecasts inherently uncertain.'] },
  ];

  const qaSteps = [
    'Baseline model verified against survey and site notes.',
    'Each measure confirmed as isolated change from baseline — no measure stacking at League Table stage.',
    'Metric extraction cross-checked against model export reports.',
    'League Table reviewed for anomalies (SBEM lighting-worsening, negative CO₂ etc.).',
    'Raft cumulation verified by comparing isolated vs cumulative runs — no arithmetic addition.',
    'Every figure in every report traced back to a model run or a documented calculation.',
  ];

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: routeContent.faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'Energy Performance Direct League Table and Rafts Methodology',
    description: 'Systematic, evidence-based methodology for domestic and non-domestic energy improvement advice using isolated measure modelling, five-criterion ranking and cumulative staged implementation (Rafts).',
    step: [
      { '@type': 'HowToStep', name: 'Baseline model', text: 'Establish RdSAP 10 or SBEM baseline from the existing or new EPC assessment.' },
      { '@type': 'HowToStep', name: 'Isolated measure runs', text: 'Model each potential improvement measure separately against the baseline — no stacking at this stage.' },
      { '@type': 'HowToStep', name: 'Extract standardised metrics', text: 'Pull EPC band and score, kWh/m²/yr, CO₂, annual cost from each measure run.' },
      { '@type': 'HowToStep', name: 'Score across five criteria', text: 'Rank each measure across EPC points, energy saving, CO₂ reduction, cost saving and payback (with 7-year test for non-domestic).' },
      { '@type': 'HowToStep', name: 'Assign to Rafts', text: 'Group top-ranked measures into three cumulative implementation stages: Raft 1 priority, Raft 2 Band C target, Raft 3 deep retrofit.' },
      { '@type': 'HowToStep', name: 'Model each Raft cumulatively', text: 'Re-run each Raft as a stacked scenario in RdSAP / SBEM to produce true combined outputs.' },
      { '@type': 'HowToStep', name: 'Deliver report', text: 'Provide baseline, three raft pathways, costed and modelled, with band outcomes for each stage.' },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Our Energy Improvement Methodology — League Table &amp; Rafts | DEAR &amp; NDEAR | Energy Performance Direct</title>
        <meta name="description" content="How Energy Performance Direct produces DEAR and NDEAR energy improvement advice reports. Isolated measure modelling, five-criterion League Table ranking, cumulative Raft implementation pathways. RdSAP 10 and iSBEM." />
        <link rel="canonical" href={canonical} />
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-green-900 text-white pt-28 pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-green-500/20 border border-green-400/40 text-green-200 text-xs font-semibold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              Independent building energy consultancy
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              The <span className="text-green-400">League Table</span> &amp; <span className="text-green-400">Rafts</span> methodology
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 max-w-3xl mx-auto leading-relaxed">
              A systematic, evidence-based framework for ranking every possible energy-improvement measure and turning the ranked list into three costed, cumulative implementation pathways. Applied consistently across every DEAR and NDEAR report we lodge.
            </p>
            <p className="text-sm text-gray-300 mb-6" data-testid="methodology-byline">
              Authored by{' '}
              <Link to="/author/zak-henning" className="text-green-300 underline">
                Zak Henning
              </Link>
              , Managing Director & Energy Assessor · Quidos accreditation QUID201039.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/quick-quote?source=methodology" data-testid="method-hero-quote-cta">
                <Button size="lg" className="bg-green-500 hover:bg-green-400 text-slate-900 px-8 py-6 text-base font-semibold rounded-full">Commission an advice report</Button>
              </Link>
              <a href={`tel:${phone.replace(/\s/g, '')}`}>
                <Button size="lg" variant="outline" className="border-2 border-white/40 text-white hover:bg-white/10 px-8 py-6 text-base rounded-full">
                  <Phone className="w-4 h-4 mr-2" /> {phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Three service families */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3 text-center">Two advice-report families</h2>
            <p className="text-base text-gray-600 text-center max-w-2xl mx-auto mb-10">
              Same methodology, two lenses. Choose the report that matches the property and the purpose.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {services.map((s, i) => (
                <Card key={i} className="border-2 hover:border-green-500 transition-all">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="text-3xl font-extrabold text-green-600 mb-1">{s.code}</div>
                    <div className="text-sm font-semibold text-gray-900 mb-3">{s.full}</div>
                    <p className="text-sm text-gray-700 mb-2"><strong>For:</strong> {s.for}</p>
                    <p className="text-sm text-gray-700 mb-4"><strong>Model:</strong> {s.model}</p>
                    <Link to={s.linkTo} className="mt-auto text-sm font-semibold text-green-700 hover:underline">Related service &rarr;</Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stage 1: League Table */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">1</div>
              <ListOrdered className="w-7 h-7 text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900">Stage 1 &mdash; The League Table</h2>
            </div>
            <p className="text-base text-gray-700 mb-8 max-w-3xl leading-relaxed">
              We start by establishing a baseline energy model of the property in RdSAP 10 (domestic) or iSBEM (non-domestic). Every candidate improvement measure is then modelled <strong>separately</strong>, as a single change against the baseline. Measures are not stacked at this stage &mdash; because measure interactions in SBEM and RdSAP are non-linear, premature stacking hides which measure actually delivered which result.
            </p>
            <h3 className="text-xl font-bold text-gray-900 mb-4">Five scoring criteria</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-8">
              {criteria.map((c, i) => (
                <div key={i} className="flex gap-3 items-start p-4 bg-white rounded-lg border">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-sm">{i + 1}</div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{c.label}</div>
                    <div className="text-xs text-gray-600 mt-1">{c.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 bg-blue-50 rounded-lg border-l-4 border-blue-600">
              <p className="text-sm text-gray-800 leading-relaxed">
                <strong>Why BER, not TER?</strong> For non-domestic advice we use <strong>BER (Building Emission Rate)</strong> as the carbon comparison basis &mdash; it reflects the actual as-modelled building performance independent of the notional target. TER only shows headroom against a moving compliance target; BER shows real measure impact.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stage 2: Rafts */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">2</div>
              <Layers className="w-7 h-7 text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900">Stage 2 &mdash; The Rafts</h2>
            </div>
            <p className="text-base text-gray-700 mb-8 max-w-3xl leading-relaxed">
              Once the League Table is complete we group the highest-ranked measures into three <strong>cumulative</strong> implementation stages, called Rafts. Each Raft is then re-modelled as a stacked scenario &mdash; you cannot simply add up the isolated measure results because their interactions are non-linear. Every Raft output is a genuine combined SBEM or RdSAP run.
            </p>
            <div className="space-y-4">
              {rafts.map((r, i) => (
                <div key={i} className="flex gap-5 p-5 bg-green-50 rounded-lg border-l-4 border-green-600">
                  <div className="flex-shrink-0 w-11 h-11 bg-green-600 text-white rounded-full flex items-center justify-center font-bold">{i + 1}</div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{r.title}</h3>
                    <p className="text-sm font-semibold text-green-800 mb-2">Target: {r.target}</p>
                    <p className="text-sm text-gray-700">{r.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-4 bg-gray-50 rounded-lg border">
                <div className="font-semibold text-gray-900 text-sm mb-1">Each Raft report includes</div>
                <ul className="text-xs text-gray-700 space-y-1 list-disc pl-4">
                  <li>Measures included</li>
                  <li>Total capital cost estimate</li>
                  <li>Combined annual energy cost saving</li>
                  <li>Resulting EPC band and rating</li>
                  <li>Resulting CO₂ emissions and energy consumption</li>
                </ul>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="font-semibold text-amber-900 text-sm mb-1">Arithmetic addition is not valid</div>
                <p className="text-xs text-amber-800">SBEM and RdSAP measure interactions are non-linear. Simply summing individual League Table results overstates or understates real outcomes. Every Raft is a full re-run of the model with all cumulative measures applied together.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regulatory context */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-center">Regulatory context we work to</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-white mb-3">Domestic MEES</h3>
                  <ul className="text-sm text-gray-200 space-y-2">
                    <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>Current minimum: EPC Band E (until Oct 2030)</span></li>
                    <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>Confirmed: Band C from 1 October 2030 for all tenancies</span></li>
                    <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>Cost cap: £10,000 per property (or 10% of value under £100k)</span></li>
                    <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>Registered exemptions available (cost cap, third-party consent, devaluation, etc.)</span></li>
                  </ul>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <h3 className="text-lg font-bold text-white mb-3">Non-Domestic MEES</h3>
                  <ul className="text-sm text-gray-200 space-y-2">
                    <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>Current minimum: EPC Band E (since 1 April 2023, all lettings)</span></li>
                    <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>7-year payback test — statutory exemption route</span></li>
                    <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>Proposed EPC B by 2031 — buildings over 1,000 m² only, pending secondary legislation (DESNZ June 2026 interim response). 2027 EPC C milestone dropped.</span></li>
                    <li className="flex gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" /><span>Penalties calculated on rateable value & breach duration (Reg 39, EEPRPR 2015)</span></li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Limitations */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
              <h2 className="text-3xl font-bold text-gray-900">Known limitations &mdash; declared upfront</h2>
            </div>
            <p className="text-base text-gray-700 mb-8 max-w-3xl">
              Independent consultancy means telling you what the models can and cannot do. Every advice report we issue carries a plain-English statement of these limitations so decisions are made with the full context.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {limitations.map((l, i) => (
                <Card key={i} className="border-2 border-amber-200 bg-amber-50/40">
                  <CardContent className="p-5">
                    <h3 className="text-base font-bold text-gray-900 mb-3">{l.title}</h3>
                    <ul className="text-sm text-gray-700 space-y-2 list-disc pl-4">
                      {l.points.map((p, j) => (<li key={j}>{p}</li>))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Quality assurance */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="w-7 h-7 text-green-600" />
              <h2 className="text-3xl font-bold text-gray-900">Quality assurance</h2>
            </div>
            <p className="text-base text-gray-700 mb-8">Six checks applied to every advice report before it leaves us.</p>
            <ul className="space-y-3">
              {qaSteps.map((s, i) => (
                <li key={i} className="flex gap-3 items-start p-4 bg-white rounded-lg border">
                  <div className="flex-shrink-0 w-7 h-7 bg-green-100 text-green-700 rounded-full flex items-center justify-center font-bold text-sm">{i + 1}</div>
                  <span className="text-sm text-gray-800 leading-relaxed">{s}</span>
                </li>
              ))}
            </ul>
            <div className="mt-6 p-5 bg-white rounded-lg border-l-4 border-green-600 flex gap-3 items-start">
              <Award className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-800"><strong>Accreditation:</strong> Domestic and non-domestic energy assessments lodged under Quidos accreditation <span className="font-mono">QUID201039</span>. The League Table + Rafts methodology is a proprietary Energy Performance Direct process, publicly documented here for transparency.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-10 text-center">Methodology questions</h2>
            <div className="space-y-4">
              {faqs.map((f, i) => (
                <details key={i} className="bg-gray-50 p-5 rounded-lg border" data-testid={`method-faq-${i}`}>
                  <summary className="font-bold text-gray-900 cursor-pointer">{f.q}</summary>
                  <div className="mt-3 text-sm text-gray-700 leading-relaxed">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-700 to-green-500 text-white">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <FileText className="w-10 h-10 mx-auto mb-4 text-green-100" />
          <h2 className="text-3xl sm:text-4xl font-bold mb-5">Commission a DEAR or NDEAR advice report</h2>
          <p className="text-lg text-green-50 mb-8">Baseline + three costed Raft pathways + full League Table working file. Tell us the property and the purpose and we'll scope the report.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/quick-quote?source=methodology" data-testid="method-cta-quote">
              <Button size="lg" className="bg-white text-green-700 hover:bg-green-50 px-8 py-6 text-base font-semibold rounded-full">Get a quote</Button>
            </Link>
            <a href={`tel:${phone.replace(/\s/g, '')}`}>
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-base rounded-full">
                <Phone className="w-4 h-4 mr-2" /> {phone}
              </Button>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Methodology;
